/**
 * S6 — Portfolio (/agg?view=overview).
 *
 * Rebuilt whole-screen to the approved figure (Story #5648 build v2). The
 * Overview is: header, next-step banner, the tab strip, the KPI strip, then
 * two columns — the Portfolio-entities table on the left, and a right rail of
 * "Possible duplicated effort", "Inbound to triage" and "Yours — not part of
 * Portfolio". Nothing else. The v1 build kept the old cohort table and the
 * old per-project grid underneath the new cards; both are gone from this page.
 *
 * Every number is derived from a live query. Where the real account has
 * nothing, the honest empty state says so — no invented row ever appears.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Building2, Compass, Download, Eye, FileCheck, Grid3x3, Inbox, Layers, LayoutGrid, Map as MapIcon } from 'lucide-react'
import HelpPopover from '../../components/help/HelpPopover'
import GuidedTour, { type TourStep } from '../../components/help/GuidedTour'
import { onTourRequest } from '../../components/help/helpBus'
import StatTile from '../../components/home/StatTile'
import SynergyMatrix from '../../components/whitespace/SynergyMatrix'
import PortfolioAtlas from '../../components/whitespace/PortfolioAtlas'
import TopologyStrategy from '../../components/whitespace/TopologyStrategy'
import PortfolioWatchPanel from '../../components/whitespace/PortfolioWatchPanel'
import OverlapView from '../../components/whitespace/OverlapView'
import { NextStepBanner, useDefaultLevels, useSetSpine } from '../../components/spine'
import { useAggregator } from '../../contexts/AggregatorContext'
import {
  getAggregatorPortfolioProjects,
  getAggregatorEntities,
  getAggregatorDisclosures,
  listEntityIpArrangements,
  getPortfolioOverlap,
  getProjects,
} from '../../services/api'
import { buildEntityGroups, entityGroupPath } from '../../components/aggregator/entityGroups'
import PortfolioEntities from '../../components/aggregator/PortfolioEntities'
import DuplicatedEffortCard, { newGroupCount } from '../../components/aggregator/DuplicatedEffortCard'
import InboundToTriageCard, { scoredTodayCount } from '../../components/aggregator/InboundToTriageCard'
import YoursNotPartOfPortfolio from '../../components/aggregator/YoursNotPartOfPortfolio'
import { buildPortfolioEntityRows, rowsToCsv } from '../../components/aggregator/portfolioRows'
import {
  countEntitiesOnLadders, entitiesOnLaddersLine,
} from '../../components/aggregator/disclosureRows'
import type { RowOrder } from '../../components/aggregator/portfolioRows'
import { useIntakeTags } from '../../components/aggregator/intake'
import { useEntityGroupNames } from '../../components/aggregator/entityGroupNames'
import { useAggregatorPreferences } from '../../hooks/useAggregatorPreferences'
import { useAggregatorAccessGate } from '../../hooks/useAggregatorAccessGate'
import type { SharingEntity } from '../../components/aggregator/sharing'
import type { IpArrangementHolderRow } from '../../types/ipArrangements'
import type { AggregatorPortfolioData, DisclosureAgreement } from '../../types'

type PortfolioView = 'overview' | 'atlas' | 'synergy' | 'overlap' | 'topology' | 'watch'

const VALID_PORTFOLIO_VIEWS: PortfolioView[] = ['overview', 'atlas', 'synergy', 'overlap', 'topology', 'watch']
const READING_PORTFOLIO_TOUR_ID = 'reading-portfolio'

/**
 * Count of inbound (pending-review) disclosures in the portfolio payload. This
 * is the SAME set the Inbound screen ranks, so the number the banner quotes
 * always equals the number of rows the user then triages.
 */
export function countInboundToday(data: AggregatorPortfolioData | undefined): number {
  if (!data) return 0
  return (data.projects ?? []).filter((p) => p.review_status === 'pending').length
}

/**
 * How many distinct things are waiting at THIS level, for the focus rail's
 * dot and its "N steps waiting" tail. Two kinds of step can wait at Portfolio:
 * overlap groups nobody has looked at, and inbound nobody has triaged. Each
 * counts once whether it holds one item or fifty — the rail says how many
 * things need a decision, not how big each one is.
 */
export function portfolioStepsWaiting(newOverlapGroups: number, inbound: number): number {
  return (newOverlapGroups > 0 ? 1 : 0) + (inbound > 0 ? 1 : 0)
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const TABS: { id: PortfolioView; label: string; icon: typeof LayoutGrid }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'atlas', label: 'Portfolio Atlas', icon: MapIcon },
  { id: 'synergy', label: 'Synergy Matrix', icon: Grid3x3 },
  { id: 'overlap', label: 'Overlap', icon: Layers },
  { id: 'topology', label: 'Shape & Strategy', icon: Compass },
  { id: 'watch', label: 'Portfolio watch', icon: Eye },
]

export default function AggregatorPortfolio() {
  const { hasAggregatorAccess, activeAggregatorId, memberships } = useAggregator()
  const accessGate = useAggregatorAccessGate('the portfolio')
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<PortfolioView>('overview')
  const [tourOpen, setTourOpen] = useState(false)
  const [overlapAutoSelect, setOverlapAutoSelect] = useState(0)
  const [pendingOverlapGroupId, setPendingOverlapGroupId] = useState<string | null>(null)

  // The table's ordering, filters and page live in the URL, not in component
  // state. Figure S6 is drawn in a filtered state ("Intake 2 ✕"), and a state
  // that only exists in memory cannot be linked to, reloaded, or captured by
  // the screen walk — round 2's capture could only ever show the unfiltered
  // table. Every one of these is absent from the URL until it is set, so the
  // bare /agg URL is unchanged.
  const order = (searchParams.get('order') as RowOrder | null) ?? 'entity'
  const intakeFilter = searchParams.get('intake')
  const stageFilter = searchParams.get('stage')
  const ipFilter = searchParams.get('ip')
  const page = Number(searchParams.get('page') ?? '0') || 0

  /** Write one table parameter, clearing it when it goes back to its default. */
  const setTableParam = (name: string, value: string | null, resetPage = true) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === null || value === '') next.delete(name)
        else next.set(name, value)
        if (resetPage) next.delete('page')
        return next
      },
      { replace: true },
    )
  }
  // The notches this viewer's ROLE gets — never a list this page decides.
  const levels = useDefaultLevels()

  useEffect(() => {
    const requested = searchParams.get('view')
    if (requested && VALID_PORTFOLIO_VIEWS.includes(requested as PortfolioView)) {
      setView(requested as PortfolioView)
    }
    const requestedGroup = searchParams.get('group')
    if (requestedGroup) setPendingOverlapGroupId(requestedGroup)
  }, [searchParams])

  const openOverlapGroup = (groupId: string) => {
    setPendingOverlapGroupId(groupId)
    setView('overlap')
  }

  const { data, isSuccess } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
    enabled: hasAggregatorAccess,
  })

  const { data: entitiesData } = useQuery({
    queryKey: ['aggregator-entities', activeAggregatorId],
    queryFn: () => getAggregatorEntities(activeAggregatorId),
    enabled: hasAggregatorAccess,
  })

  const { data: disclosuresData } = useQuery({
    queryKey: ['aggregatorDisclosures', activeAggregatorId],
    queryFn: () => getAggregatorDisclosures(activeAggregatorId ?? ''),
    enabled: hasAggregatorAccess && !!activeAggregatorId,
  })

  const { data: overlapData } = useQuery({
    queryKey: ['portfolio-overlap', activeAggregatorId],
    queryFn: () => getPortfolioOverlap(activeAggregatorId),
    enabled: hasAggregatorAccess,
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3000 : false),
  })

  // The portfolio's real name, from the caller's own membership list — the
  // AggregatorProvider already holds it, so naming the portfolio costs no
  // extra request and can never disagree with the workspace switcher.
  const portfolioName =
    memberships.find((m) => m.aggregator_id === activeAggregatorId)?.aggregator_name ?? ''

  const { data: ownProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: hasAggregatorAccess,
  })

  const { intakeTags } = useIntakeTags(activeAggregatorId)
  const { groupNames } = useEntityGroupNames(activeAggregatorId)
  // The stage this portfolio has recorded against each entity — the only stage
  // anything on the platform records. Same server-side preference store the
  // rest of the aggregator screens already read.
  const { entityTags } = useAggregatorPreferences()

  const projects = useMemo(() => data?.projects ?? [], [data])
  const entities = useMemo(() => (entitiesData?.entities ?? []) as SharingEntity[], [entitiesData])
  const disclosures = useMemo(
    () => (Array.isArray(disclosuresData) ? (disclosuresData as DisclosureAgreement[]) : []),
    [disclosuresData],
  )
  const overlapGroups = useMemo(() => overlapData?.groups ?? [], [overlapData])

  // The same group overrides C1 writes, so an inventor a portfolio re-filed on
  // the Entity screen is in that group here too.
  const entityGroups = useMemo(
    () => buildEntityGroups(entities, projects, groupNames),
    [entities, projects, groupNames],
  )
  // What is on record about IP for every entity in this portfolio, so the
  // figure's third filter is a working control rather than a greyed-out box.
  // One read per entity, on the same route S5 and the C1 drawer use, and it
  // returns counts only — never an owner's id, name or email.
  const ipEntityIds = useMemo(() => entities.map((e) => e.entity_id), [entities])
  const ipQueries = useQueries({
    queries: ipEntityIds.map((entityId) => ({
      queryKey: ['ip-arrangements', activeAggregatorId, entityId],
      queryFn: () => listEntityIpArrangements(activeAggregatorId ?? '', entityId),
      enabled: hasAggregatorAccess && !!activeAggregatorId,
    })),
  })
  const arrangementsByEntity = new Map<string, IpArrangementHolderRow[]>()
  ipEntityIds.forEach((entityId, i) => {
    arrangementsByEntity.set(entityId, ipQueries[i]?.data ?? [])
  })

  const rows = useMemo(
    () => buildPortfolioEntityRows(entityGroups, intakeTags, entityTags, arrangementsByEntity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityGroups, intakeTags, entityTags, ipQueries.map((q) => q.dataUpdatedAt).join()],
  )

  const projectNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const [id, name] of Object.entries(data?.project_names ?? {})) map.set(id, name)
    for (const p of projects) map.set(p.project_id, p.name)
    return map
  }, [data, projects])

  // Never list a project that is already granted to the portfolio: an
  // invention appears once, in the portfolio, never twice. getProjects returns
  // owned AND shared-with-me rows, and only the OWNED ones are "yours".
  const portfolioProjectIds = useMemo(
    () => new Set(projects.map((p) => p.project_id)),
    [projects],
  )
  const ownProjectsNotInPortfolio = useMemo(
    () => (ownProjects ?? []).filter((p) => !p.shared_with_me && !portfolioProjectIds.has(p.project_id)),
    [ownProjects, portfolioProjectIds],
  )

  const inboundProjects = useMemo(
    () => projects.filter((p) => p.review_status === 'pending'),
    [projects],
  )
  const inboundCount = inboundProjects.length
  // The figure's fourth tile carries "scored today". A real queue is mostly
  // older than that, so the tile says how many really were — and says "none
  // scored today" rather than dropping the line and leaving a bare number.
  const scoredToday = scoredTodayCount(inboundProjects)
  const byInvitationCount = rows.filter((r) => r.sharing.byInvitation).length
  // ONE definition, shared with S7 (disclosureRows.countEntitiesOnLadders):
  // distinct entities of THIS portfolio that are a party to a live disclosure.
  // Round 2 counted distinct `my_org_id` here and said "across 3 entities"
  // while S7's own table showed two — `my_org_id` is a per-caller field, not
  // an entity list.
  const ownEntityIds = useMemo(() => {
    const ids = new Set<string>()
    for (const e of entities) ids.add(e.entity_id)
    for (const p of projects) if (p.entity_id) ids.add(p.entity_id)
    return ids
  }, [entities, projects])
  const disclosureEntityCount = countEntitiesOnLadders(disclosures, ownEntityIds)
  const newOverlapCount = newGroupCount(overlapGroups)
  const stepsWaiting = portfolioStepsWaiting(newOverlapCount, inboundCount)

  // The rail's zoom-in goes to the first entity the table is showing, which is
  // the row a reader's eye is already on. With no entities there is nothing to
  // zoom into and the arrow says so instead of pointing nowhere.
  const firstRow = rows[0]

  useSetSpine(
    hasAggregatorAccess
      ? {
          level: 'portfolio',
          levels,
          youreAt: [portfolioName].filter(Boolean),
          stepsWaiting,
          zoomIn: firstRow
            ? { title: `Zoom in: Entity — ${firstRow.label}`, to: entityGroupPath(firstRow.label) }
            : { title: 'Zoom in: Entity — no entity in this portfolio yet' },
          zoomOut: { title: 'Zoom out: Across portfolios', to: '/agg/across' },
          crumb: [portfolioName || 'Portfolio', 'Portfolio'],
        }
      : null,
  )

  // Replay of the "Reading your portfolio" tour, requested from the Resources
  // panel. It no longer auto-opens: the figure shows no overlay on this screen,
  // and a tour that opens itself over the Overview covers the very thing it is
  // explaining.
  useEffect(
    () =>
      onTourRequest((tourId) => {
        if (tourId === READING_PORTFOLIO_TOUR_ID) setTourOpen(true)
      }),
    [],
  )

  const portfolioTourSteps: TourStep[] = useMemo(
    () => [
      {
        target: 'portfolio-atlas-map',
        title: 'One map of everything your portfolio knows',
        body: 'Every project you can see, plotted together. Each colour is a project — the coloured dots are its documents, projected so nearby ideas sit near each other.',
        onBeforeStep: () => setView('atlas'),
      },
      {
        target: 'portfolio-atlas-members',
        title: 'Footprints: where each project actually is',
        body: 'The line traced around a project on the map is its footprint — the real boundary of its own ground. When two footprints overlap, that is your first visual clue two projects may be working the same territory.',
        onBeforeStep: () => setView('atlas'),
      },
      {
        target: 'overlap-group-list',
        title: 'Synergy vs. Overlap: two different questions',
        body: 'Synergy finds projects that COMPLEMENT each other — combine them and you get something neither has alone. Overlap, here, finds projects DUPLICATING each other — working the same ground, which usually means duplicated funding asks.',
        onBeforeStep: () => setView('overlap'),
      },
      {
        target: 'portfolio-shape-tiles',
        title: 'Portfolio shape at a glance',
        body: 'Holes and bridges your project scans found, rolled up across the whole portfolio, plus how many candidate shapes were discarded as flattened-map artefacts before they ever reached this tile.',
        onBeforeStep: () => setView('atlas'),
      },
      {
        target: 'overlap-group-detail',
        title: 'Fund once, not several times',
        body: 'Open a group and this panel shows its members and status. Working a group through Reviewing to Consolidated is how you turn "several projects, several funding asks" into one consolidated case.',
        onBeforeStep: () => {
          setView('overlap')
          setOverlapAutoSelect(Date.now())
        },
      },
    ],
    [],
  )

  if (accessGate) return <div data-part="AggregatorPortfolio">{accessGate}</div>

  const banner =
    newOverlapCount > 0 ? (
      <NextStepBanner
        at="Portfolio"
        verb="review overlap"
        headline={`${newOverlapCount} new group${newOverlapCount === 1 ? '' : 's'} of projects working the same ground — review ${newOverlapCount === 1 ? 'it' : 'them'} before they are funded twice.`}
        rule="from your overlap scan · computed over everything you're entitled to see · scope: this portfolio"
        tone="amber"
        cta={{ label: 'Open Overlap →', onClick: () => setView('overlap') }}
      />
    ) : inboundCount > 0 ? (
      <NextStepBanner
        at="Portfolio"
        verb="triage"
        headline={`${inboundCount} disclosure${inboundCount === 1 ? '' : 's'} arrived and ${inboundCount === 1 ? 'has' : 'have'} not been reviewed — triage ${inboundCount === 1 ? 'it' : 'them'}.`}
        rule="from inbound · scope: this portfolio"
        tone="amber"
        cta={{ label: 'Triage inbound →', to: '/agg/inbound' }}
      />
    ) : (
      <NextStepBanner
        at="Portfolio"
        verb="review overlap"
        none
        headline="Nothing is waiting at Portfolio. New inbound and new overlap groups appear here as they arrive."
        rule="from your overlap scan and inbound · scope: this portfolio"
      />
    )

  return (
    <div data-part="AggregatorPortfolio">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-iflow-900">Portfolio</h1>
            <span
              className="rounded-full bg-iflow-100 px-2.5 py-0.5 text-[12.5px] font-semibold text-iflow-700"
              data-testid="portfolio-entity-chip"
            >
              {rows.length} {rows.length === 1 ? 'entity' : 'entities'}
            </span>
            <HelpPopover topic="agg-portfolio" size={18} />
          </div>
          <p className="mt-1 text-sm text-slate-600">
            The innovations across the organisations you manage — triage inbound and act on them in bulk.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => downloadCsv('portfolio-entities.csv', rowsToCsv(rows))}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-iflow-900/15 bg-white px-3 py-2 text-[13px] font-semibold text-iflow-800 disabled:opacity-40"
          >
            <Download size={14} aria-hidden /> Export CSV
          </button>
          <Link
            to="/agg/inbound"
            className="inline-flex items-center gap-1.5 rounded-lg bg-iflow-600 px-3.5 py-2 text-[13px] font-bold text-white hover:bg-iflow-700"
          >
            <Inbox size={14} aria-hidden /> Triage inbound
          </Link>
        </div>
      </div>

      {banner}

      <div className="mb-4 flex flex-wrap gap-0.5 rounded-lg bg-sky-500/10 p-0.5 w-fit" role="tablist" aria-label="Portfolio view">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            role="tab"
            aria-selected={view === id}
            onClick={() => setView(id)}
            className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              view === id ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-3 w-3" /> {label}
            {id === 'overlap' && newOverlapCount > 0 && (
              <span className="ml-1 rounded-full bg-iflow-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-iflow-700">
                {newOverlapCount} new
              </span>
            )}
          </button>
        ))}
      </div>

      {view === 'atlas' && (
        <PortfolioAtlas onOpenTopologyStrategy={() => setView('topology')} onOpenOverlapGroup={openOverlapGroup} />
      )}
      {view === 'synergy' && <SynergyMatrix onOpenOverlapGroup={openOverlapGroup} />}
      {view === 'overlap' && (
        <OverlapView
          initialGroupId={pendingOverlapGroupId}
          onOpenSynergy={() => setView('synergy')}
          autoSelectSignal={overlapAutoSelect}
        />
      )}
      {view === 'topology' && <TopologyStrategy />}
      {view === 'watch' && <PortfolioWatchPanel />}

      {view === 'overview' && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="portfolio-kpi-tiles">
            <StatTile
              icon={Building2}
              label="Portfolio entities"
              value={rows.length}
              caption={
                byInvitationCount > 0
                  ? `${byInvitationCount} by invitation`
                  : rows.length > 0
                    ? 'all joined by sharing'
                    : undefined
              }
            />
            <StatTile
              icon={FileCheck}
              label="Live disclosures"
              value={disclosures.length}
              caption={
                disclosureEntityCount > 0
                  ? entitiesOnLaddersLine(disclosureEntityCount)
                  : undefined
              }
            />
            <StatTile
              icon={Layers}
              label="Overlap groups"
              value={overlapGroups.length}
              caption={newOverlapCount > 0 ? `${newOverlapCount} new — review` : undefined}
            />
            <StatTile
              icon={Inbox}
              label="Inbound to triage"
              value={inboundCount}
              caption={
                !isSuccess
                  ? undefined
                  : scoredToday > 0
                    ? `${scoredToday} scored today`
                    : inboundCount > 0
                      ? 'none scored today'
                      : 'nothing waiting'
              }
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <PortfolioEntities
                rows={rows}
                portfolioName={portfolioName}
                order={order}
                onOrderChange={(next) => setTableParam('order', next === 'entity' ? null : next)}
                intakeFilter={intakeFilter}
                onIntakeFilterChange={(next) => setTableParam('intake', next)}
                stageFilter={stageFilter}
                onStageFilterChange={(next) => setTableParam('stage', next)}
                ipFilter={ipFilter}
                onIpFilterChange={(next) => setTableParam('ip', next)}
                page={page}
                onPageChange={(next) => setTableParam('page', next > 0 ? String(next) : null, false)}
              />
            </div>
            <div className="flex flex-col gap-4">
              <DuplicatedEffortCard
                groups={overlapGroups}
                projectNames={projectNames}
                computing={overlapData?.status === 'processing'}
                onOpenOverlap={() => setView('overlap')}
                onOpenGroup={openOverlapGroup}
              />
              <InboundToTriageCard projects={inboundProjects} />
              <YoursNotPartOfPortfolio
                projects={ownProjectsNotInPortfolio}
                portfolioName={portfolioName}
              />
            </div>
          </div>
        </>
      )}

      <GuidedTour
        tourId={READING_PORTFOLIO_TOUR_ID}
        steps={portfolioTourSteps}
        open={tourOpen}
        onClose={() => setTourOpen(false)}
      />
    </div>
  )
}
